import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';

import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';
import { NavFlyoutEmptyState } from '@atlassian/global-side-navigation/NavFlyoutEmptyState';

import { AddFeaturesSidebarSpotlight } from '@confluence/experiment-cfind-4473-creation-focused-nav/entry-points/AddFeaturesSidebarSpotlight.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';
import { SimplifyNavNewUserSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/SimplifyNavNewUserSpotlight.ts';
import { useSimplifiedNavNewUsers } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavNewUsers.ts';
import { useSimplifiedNavSpotlight } from '@confluence/experiment-simplify-nav-new-user/entry-points/useSimplifiedNavSpotlight.ts';
import { fg } from '@confluence/feature-gating';
import { APP_NAV_PAGES_DROPDOWN_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStop,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { expVal, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import {
	GO_HOME_SHORTCUT,
	GO_TO_RECENT_SHORTCUT,
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import { preloadRecentContentMenu } from '../../ContentMenu/preloadRecentContentMenu.ts';
import { preloadStarredContentMenu } from '../../ContentMenu/preloadStarredContentMenu.ts';
import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';
import { DiscoErrorBoundary } from '../components/DiscoErrorBoundary.tsx';

import { RecentContentMenuLoader } from './RecentContentMenuLoader.tsx';
import { RecentsEmptyStateLoader } from './RecentsEmptyStateLoader.tsx';
import { StarredContentMenuLoader } from './StarredContentMenuLoader.tsx';
import { StarredEmptyStateLoader } from './StarredEmptyStateLoader.tsx';
import { useRecentItems, useRecentsFilterConfig } from './useRecentItems.tsx';
import { useStarredItems } from './useStarredItems.tsx';

const recentAttributes = { navVersion: '4', dropdownType: 'recent' };

// This wrapper is the Recent item's ItemWrapper, so it only renders when GSN renders Recent — i.e.
// Recent is in the nav. useSimplifiedNavSpotlight derives isRecentInMainNav by checking for a
// 'recent' entry, so we pass a single-item list to signal "Recent is present".
const RECENT_IN_MAIN_NAV: ReadonlyArray<{ menuId: string }> = [{ menuId: 'recent' }];

// GSN's <nav> has no stable id, so spotlights observe its (non-localized) aria-label to reposition
// on nav mutations.
const GSN_NAV_SELECTORS = ['nav[aria-label="Sidebar"]'];
const starredAttributes = { navVersion: '4', dropdownType: 'starred' };

const StopRecent = () => {
	return <ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={recentAttributes} />;
};

const StopStarred = () => (
	<ExperienceStop name={APP_NAV_PAGES_DROPDOWN_EXPERIENCE} attributes={starredAttributes} />
);

const RecentEmptyStateExperiment = (): React.JSX.Element =>
	expVal('cc_mohiti_nav_empty_states', 'isEnabled', false) ? (
		<DiscoErrorBoundary>
			<RecentsEmptyStateLoader />
		</DiscoErrorBoundary>
	) : (
		<NavFlyoutEmptyState />
	);

const StarredEmptyStateExperiment = (): React.JSX.Element =>
	expVal('cc_mohiti_nav_empty_states', 'isEnabled', false) ? (
		<DiscoErrorBoundary>
			<StarredEmptyStateLoader />
		</DiscoErrorBoundary>
	) : (
		<NavFlyoutEmptyState variant="starred" />
	);

type ForYouItemWrapperProps = {
	children: React.ReactNode;
	deprecatedKeyboardShortcut: string | string[] | undefined;
	homeUrl: string;
};

type GenericItemWrapperProps = {
	children: React.ReactNode;
	deprecatedKeyboardShortcut: string | string[] | undefined;
	keyboardShortcut: string | string[];
	onMouseEnter: () => void;
	onShortcutTriggered: () => void;
};

const ForYouItemWrapper = ({
	children,
	homeUrl,
	deprecatedKeyboardShortcut,
}: ForYouItemWrapperProps) => (
	<DiscoErrorBoundary>
		<KeyboardShortcutWrapper
			href={homeUrl}
			deprecatedKeyboardShortcut={deprecatedKeyboardShortcut}
			keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
			isAppNavigationShortcut
		>
			<HomeOnboardingTakeoverV2HomeNudge>
				<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
			</HomeOnboardingTakeoverV2HomeNudge>
		</KeyboardShortcutWrapper>
	</DiscoErrorBoundary>
);

const GenericItemWrapper = ({
	children,
	deprecatedKeyboardShortcut,
	keyboardShortcut,
	onMouseEnter,
	onShortcutTriggered,
}: GenericItemWrapperProps) => (
	<DiscoErrorBoundary>
		<div onMouseEnter={onMouseEnter} onFocus={onMouseEnter}>
			<KeyboardShortcutWrapper
				deprecatedKeyboardShortcut={deprecatedKeyboardShortcut}
				keyboardShortcut={keyboardShortcut}
				isAppNavigationShortcut
				onShortcutTriggered={onShortcutTriggered}
			>
				{children}
			</KeyboardShortcutWrapper>
		</div>
	</DiscoErrorBoundary>
);

// New-nav eligibility for the simplify-nav (5565) spotlight. Anchored to Recent, so Recent is always
// in main nav. The gate is evaluated in RecentSpotlights, not here.
const useSimplifyNavSpotlightForNewNav = (): {
	shouldShowSpotlight: boolean;
	source: 'new-user' | 'xflow' | null;
} => {
	// Skip the onboarding-state request when the gate is off (spotlights aren't rendered then).
	const { navStyle, isWithinD0, source } = useSimplifiedNavNewUsers(!fg('simplify_nav_gsn'));
	const { shouldShowSpotlight } = useSimplifiedNavSpotlight({
		globalMenuItems: RECENT_IN_MAIN_NAV,
		navStyle,
		isWithinD0,
	});

	return { shouldShowSpotlight, source };
};

/**
 * Wraps Recent with the two onboarding spotlights previously mounted in the old nav, gated by
 * `simplify_nav_gsn`:
 *  - `SimplifyNavNewUserSpotlight` (5565) — driven by parent eligibility.
 *  - `AddFeaturesSidebarSpotlight` (4473) — self-contained; reads its own onboarding state.
 */
const RecentSpotlights = ({ children }: { children: React.ReactNode }) => {
	const { shouldShowSpotlight, source } = useSimplifyNavSpotlightForNewNav();

	if (!fg('simplify_nav_gsn')) {
		return <>{children}</>;
	}

	return (
		<AddFeaturesSidebarSpotlight repositionSelectors={GSN_NAV_SELECTORS}>
			<SimplifyNavNewUserSpotlight
				shouldShowSpotlight={shouldShowSpotlight}
				source={source}
				repositionSelectors={GSN_NAV_SELECTORS}
			>
				{children}
			</SimplifyNavNewUserSpotlight>
		</AddFeaturesSidebarSpotlight>
	);
};

export const useForYouConfig = (): ForYouConfig => {
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const homeUrl = HOME.toUrl();
	const isHomeSelected = useRouteUrl(isOnHomeSelector);

	const isRecentOpenRef = useRef(false);
	const recentsFilterConfig = useRecentsFilterConfig();
	const recentViewAllUrl = HOME.toUrl({ view: 'recent' });
	const [isRecentOpen, setIsRecentOpen] = useState(false);
	const isRecentSelected = useRouteUrl(isOnRecentRoute);
	const onRecentMouseEnter = useCallback(() => {
		if (!isRecentOpenRef.current) {
			void RecentContentMenuLoader.preload();
			void preloadRecentContentMenu(cloudId);
		}
	}, [cloudId]);
	const onRecentShortcut = useCallback(() => {
		const next = !isRecentOpenRef.current;
		isRecentOpenRef.current = next;
		setIsRecentOpen(next);
		if (next) {
			isStarredOpenRef.current = false;
			setIsStarredOpen(false);
		}
	}, []);

	const isStarredOpenRef = useRef(false);
	const starredViewAllUrl = HOME.toUrl({ view: 'starred' });
	const [isStarredOpen, setIsStarredOpen] = useState(false);
	const isStarredSelected = useRouteUrl(isOnStarredRoute);
	const onStarredMouseEnter = useCallback(() => {
		if (!isStarredOpenRef.current) {
			void StarredContentMenuLoader.preload();
			void preloadStarredContentMenu();
		}
	}, []);
	const onStarredShortcut = useCallback(() => {
		const next = !isStarredOpenRef.current;
		isStarredOpenRef.current = next;
		setIsStarredOpen(next);
		if (next) {
			isRecentOpenRef.current = false;
			setIsRecentOpen(false);
		}
	}, []);

	const experienceTracker = useContext(ExperienceTrackerContext);

	const startExperienceTracker = useCallback(
		(experienceName) => {
			experienceTracker.start({
				name: APP_NAV_PAGES_DROPDOWN_EXPERIENCE,
				timeout: ExperienceTimeout.NAVIGATION_LOAD,
				attributes: {
					navVersion: '4',
					dropdownType: experienceName,
				},
			});
		},
		[experienceTracker],
	);

	const onRecentOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) startExperienceTracker('recent');
			isRecentOpenRef.current = isOpen;
			setIsRecentOpen(isOpen);
		},
		[startExperienceTracker],
	);
	const onStarredOpenChange = useCallback(
		(isOpen: boolean) => {
			if (isOpen) startExperienceTracker('starred');
			isStarredOpenRef.current = isOpen;
			setIsStarredOpen(isOpen);
		},
		[startExperienceTracker],
	);

	const isKeyboardShortcutEnabled = !(isLiveEditMode || isOnEditRoute);

	const ForYouWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<ForYouItemWrapper
				{...props}
				homeUrl={homeUrl}
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_HOME_SHORTCUT : undefined}
			/>
		),
		[homeUrl, isKeyboardShortcutEnabled],
	);

	const RecentWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<GenericItemWrapper
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_TO_RECENT_SHORTCUT : undefined}
				keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
				onMouseEnter={onRecentMouseEnter}
				onShortcutTriggered={onRecentShortcut}
			>
				<RecentSpotlights>{props.children}</RecentSpotlights>
			</GenericItemWrapper>
		),
		[isKeyboardShortcutEnabled, onRecentMouseEnter, onRecentShortcut],
	);

	const StarredWrapper = useCallback(
		(props: { children: React.ReactNode }) => (
			<GenericItemWrapper
				{...props}
				deprecatedKeyboardShortcut={isKeyboardShortcutEnabled ? GO_TO_STARRED_SHORTCUT : undefined}
				keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
				onMouseEnter={onStarredMouseEnter}
				onShortcutTriggered={onStarredShortcut}
			/>
		),
		[isKeyboardShortcutEnabled, onStarredMouseEnter, onStarredShortcut],
	);

	return useMemo<ForYouConfig>(
		() => ({
			forYouItem: {
				type: 'link' as const,
				menuItemId: 'yourWork',
				url: homeUrl,
				isSelected: isHomeSelected,
				ItemWrapper: ForYouWrapper,
			},
			recent: {
				menuItemId: 'recent',
				useItems: useRecentItems,
				viewAllUrl: recentViewAllUrl,
				hideActionsWhenEmpty: UNSAFE_expValNoExposure(
					'cc_mohiti_nav_empty_states',
					'isEnabled',
					false,
				),
				emptyElem: <RecentEmptyStateExperiment />,
				isSearchable: true,
				filterConfig: recentsFilterConfig,
				isSelected: isRecentSelected,
				isOpen: isRecentOpen,
				children: <RecentContentMenuLoader />,
				experienceEndTracker: StopRecent,
				onOpenChange: onRecentOpenChange,
				ItemWrapper: RecentWrapper,
			},
			starred: {
				menuItemId: 'starred',
				useItems: useStarredItems,
				viewAllUrl: starredViewAllUrl,
				hideActionsWhenEmpty: UNSAFE_expValNoExposure(
					'cc_mohiti_nav_empty_states',
					'isEnabled',
					false,
				),
				isSearchable: true,
				isOpen: isStarredOpen,
				isSelected: isStarredSelected,
				children: <StarredContentMenuLoader />,
				emptyElem: <StarredEmptyStateExperiment />,
				experienceEndTracker: StopStarred,
				onOpenChange: onStarredOpenChange,
				ItemWrapper: StarredWrapper,
			},
		}),
		[
			homeUrl,
			isHomeSelected,
			isRecentOpen,
			isRecentSelected,
			isStarredOpen,
			isStarredSelected,
			recentViewAllUrl,
			recentsFilterConfig,
			starredViewAllUrl,
			onRecentOpenChange,
			onStarredOpenChange,
			ForYouWrapper,
			RecentWrapper,
			StarredWrapper,
		],
	);
};

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};
const isOnStarredRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'starred' }),
};
const isOnRecentRoute = {
	selector: (routeUrl: string | undefined) => routeUrl === HOME.toUrl({ view: 'recent' }),
};
